import React, { useState, useEffect } from 'react';
import { transferAPI, userAPI, authAPI } from '../../services/api';
import './MoneyTransfer.css';

const MoneyTransfer = () => {
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    fromAccount: '',
    toAccount: '',
    amount: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const user = authAPI.getCurrentUser();
        if (!user) return;

        const data = await userAPI.getUserAccounts(user.id);
        setAccounts(data.accounts || []);

        // Set first account as default
        if (data.accounts && data.accounts.length > 0) {
          setFormData(prev => ({
            ...prev,
            fromAccount: data.accounts[0].account_number
          }));
        }
      } catch (err) {
        console.error('Failed to load accounts:', err);
      }
    };

    fetchAccounts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await transferAPI.createTransfer(formData);

      setSuccess(`Transfer successful! $${formData.amount} transferred to ${formData.toAccount}`);

      // Reset form
      setFormData(prev => ({
        ...prev,
        toAccount: '',
        amount: '',
        description: ''
      }));

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = accounts.find(acc => acc.account_number === formData.fromAccount);

  return (
    <div className="money-transfer">
      <div className="transfer-header">
        <h2>Transfer Money</h2>
        <p className="transfer-subtitle">Send money to another account</p>
      </div>

      <div className="transfer-container">
        <div className="transfer-form-section">
          {success && (
            <div className="success-message">
              <span>✓</span> {success}
            </div>
          )}

          {error && (
            <div className="error-message">
              <span>⚠️</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="transfer-form">
            <div className="form-group">
              <label htmlFor="fromAccount">From Account</label>
              <select
                id="fromAccount"
                name="fromAccount"
                className="input"
                value={formData.fromAccount}
                onChange={handleChange}
                required
              >
                <option value="">Select account</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.account_number}>
                    {account.account_type.toUpperCase()} - {account.account_number} (${parseFloat(account.balance).toLocaleString()})
                  </option>
                ))}
              </select>
              {selectedAccount && (
                <div className="account-balance-info">
                  Available Balance: <strong>${parseFloat(selectedAccount.balance).toLocaleString()}</strong>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="toAccount">To Account Number</label>
              <input
                id="toAccount"
                name="toAccount"
                type="text"
                className="input"
                value={formData.toAccount}
                onChange={handleChange}
                placeholder="Enter recipient account number"
                required
              />
              <div className="input-hint">
                Enter the 10-digit account number (e.g., 1000000002)
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="amount">Amount</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  id="amount"
                  name="amount"
                  type="number"
                  className="input amount-input"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <input
                id="description"
                name="description"
                type="text"
                className="input"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter transfer description"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-large"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Transfer Money'}
            </button>
          </form>

          <div className="vulnerability-note">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>Vulnerability Demo:</strong> This transfer has no authorization checks.
              Try using someone else's account number in the "From" field via API manipulation!
            </div>
          </div>
        </div>

        <div className="transfer-info-section">
          <div className="info-card">
            <h3>Transfer Information</h3>
            <ul className="info-list">
              <li>
                <span className="info-icon">⚡</span>
                <span>Instant transfers between accounts</span>
              </li>
              <li>
                <span className="info-icon">💰</span>
                <span>No transfer fees</span>
              </li>
              <li>
                <span className="info-icon">🔓</span>
                <span><strong>Vulnerable:</strong> No balance validation</span>
              </li>
              <li>
                <span className="info-icon">🚨</span>
                <span><strong>Vulnerable:</strong> No authorization (BOLA)</span>
              </li>
            </ul>
          </div>

          <div className="info-card security-issues">
            <h3>Security Issues</h3>
            <ul className="vulnerability-list">
              <li>✗ No verification of account ownership</li>
              <li>✗ No sufficient funds check</li>
              <li>✗ No transaction locking (race conditions possible)</li>
              <li>✗ Can transfer from any account by manipulating IDs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoneyTransfer;
